import React, { useState, useMemo, useEffect, useRef, Suspense } from "react";
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
import { useDashboard } from "./dashboardStore";

import ReportExporter from "../utils/ReportExporter";
import StagingTable from "../utils/StagingTable";
import { socket } from "../utils/socket";
import FormattedNumberInput from "../utils/FormattedNumberInput";

const InvoicePDFForm = React.lazy(() =>
  import("../utils/InvoicePDFForm")
);

const fieldLabels = {
	customerName: "Customer Name",
	itemName: "Reference Tag",
	status: "Payment Term",
	invoiceNumber: "Invoice Number",
	warehouse: "Warehouse",
	region: "Region"
};

const InvoicesTable = () => {
	const [invoices, setInvoices] = useState([]);
	const [search, setSearch] = useState("");
	const [showModal, setShowModal] = useState(false);
	const [newInvoice, setNewInvoice] = useState({
		customerName: "",
		itemName: "",
		warehouse: "",
		status: "",
		region: "",
		total: 0
	});

	const [showPDFSection, setShowPDFSection] = useState(null);
	const [showStaging, setShowStaging] = useState(false);
	const [errors, setErrors] = useState({});
	const [page, setPage] = useState(1);
	const pageSize = 8;
	const [loading, setLoading] = useState(false);

	const parentRef = useRef(null);
	const stagingRef = useRef(null);

	useEffect(() => {
		if (showStaging && stagingRef.current) {
			stagingRef.current.scrollIntoView({ behavior: "smooth" });
		}
		if (!showStaging && parentRef?.current) {
			parentRef.current.scrollIntoView({ behavior: "smooth" });
		}
	}, [showStaging]);
    useEffect(() => {
    	if (!socket) return;
    
    	const handleAlertInvoices = (data) => {
    		if (data.type === "singleNewInvoice" && data.newInvoice) {
    			setInvoices((prev) => [data.newInvoice, ...prev]);
    		}
    		if (data.type === "batchInvoices" && data.rows) {
    			setInvoices((prev) => [...data.rows, ...prev]);
    		}
    		if (data.type === "invoiceDeleted" && data.id) {
    			setInvoices((prev) => prev.filter((i) => i.id !== Number(data.id)));
    		}
    	};
    
    	socket.on("alertInvoices", handleAlertInvoices);
    
    	return () => {
    		socket.off("alertInvoices", handleAlertInvoices);
    	};
    }, [socket]);
    

// Subscribe to dashboard store slice
const invoicesDat = useDashboard(state => state.data?.invoices);

// -------------------------------------
// Process mistakes requests data
// -------------------------------------
useEffect(() => {
  // Wait until store has data
  if (!invoicesDat) return;

  try {
    // Save to state
			setInvoices(Array.isArray(invoicesDat) ? invoicesDat : []);
  } catch (err) {
    console.error("Error processing feature requests:", err);
    setItems([]);
  }
}, [invoicesDat]); // Reactive: runs whenever store updates


	const validateInvoice = () => {
		const newErrors = {};
		Object.entries(newInvoice).forEach(([key, value]) => {
            if (key !== "createdAt" && key !== "total" && !value) newErrors[key] = true;

		});
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleAdd = async (inv) => {
		if (!validateInvoice()) return;
		try {
			const res = await fetch(`${apiBaseUrl}/api/invoices`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					customerName: inv.customerName,
					itemName: inv.itemName,
					warehouse: inv.warehouse,
					status: inv.status,
					region: inv.region
				})
			});
			if (!res.ok) throw new Error("Failed to add invoice");
			setShowModal(false);
			setNewInvoice({
				customerName: "",
				itemName: "",
				warehouse: "",
				status: "",
				region: "",

				total: 0
			});
			setErrors({});
		} catch (err) {
			console.error(err);
			alert("Failed to add invoice. Try again.");
		}
	};

	const handleDelete = async (id) => {
		if (!window.confirm("⚠️ Delete this invoice?")) return;

		try {
			const res = await fetch(`${apiBaseUrl}/api/invoices/${id}`, { method: "DELETE" });
			if (!res.ok) throw new Error("Failed to delete invoice");
		} catch (err) {
			console.error(err);
			alert("Failed to delete invoice. Try again.");
		}
	};

	const filteredInvoices = useMemo(() => {
		const safe = Array.isArray(invoices) ? invoices : [];
		const s = search.toLowerCase();
		return safe.filter(
			(i) =>
				(i.customerName?.toLowerCase() ?? "").includes(s) ||
				(i.itemName?.toLowerCase() ?? "").includes(s) ||
				(i.warehouse?.toLowerCase() ?? "").includes(s) ||
				(i.status?.toLowerCase() ?? "").includes(s)
		);
	}, [invoices, search]);

	const totalPages = Math.ceil((filteredInvoices?.length || 0) / pageSize);
	const paginatedInvoices = useMemo(() => {
		const start = (page - 1) * pageSize;
		return (filteredInvoices || []).slice(start, start + pageSize);
	}, [filteredInvoices, page]);

	return (
		<div className="w-full  flex-1 overflow-y-auto p-4">
			{/* Exporter */}
			<div className="md:w-1/2 ml-auto mb-4">
				<ReportExporter
					title="Invoices Export"
					data={invoices}
					columns={Object.entries(fieldLabels).map(([field, label]) => ({ label, field }))}
					filterableFields={fieldLabels}
				/>
			</div>

			{/* Search + Add + Batch */}
			<div ref={parentRef} className="flex flex-col sm:flex-row items-center justify-between mb-4 gap-2">
				<input
					type="text"
					placeholder="Search by customer, invoice, item, warehouse or status..."
					className="px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring focus:ring-blue-200 flex-1"
					value={search}
					onChange={(e) => {
						setSearch(e.target.value);
						setPage(1);
					}}
				/>
				<button
					className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
					onClick={() => setShowStaging((prev) => !prev)}
				>
					Batch Upload
					<span
						className={`ml-2 transform transition-transform duration-200 ${
							showStaging ? "rotate-180" : "rotate-0"
						}`}
					>
						▼
					</span>
				</button>
				<button
					className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
					onClick={() => setShowModal(true)}
				>
					Add Invoice
				</button>
			</div>

			{/* Table */}
			<div className="overflow-x-auto rounded-md border border-gray-200">
				<table className="min-w-full divide-y divide-gray-100 text-sm md:text-base">
					<thead className="bg-gray-50">
						<tr>
							<th className="px-3 py-4 text-left font-medium text-gray-700">#</th>
							{Object.values(fieldLabels).map((label) => (
								<th key={label} className="px-3 py-4 text-center font-medium text-gray-700">
									{label}
								</th>
							))}
							<th className="px-3 py-4 text-center font-medium text-gray-700">Total</th>
							<th className="px-3 py-4 text-center font-medium text-gray-700">VAT (5%)</th>
							<th className="px-3 py-4 text-center font-medium text-gray-700">Date Created</th>
							<th className="px-3 py-4 text-center font-medium text-gray-700">Actions</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-gray-100">
						{paginatedInvoices.length === 0 && (
							<tr>
								<td colSpan={10} className="px-3 py-4 text-center text-gray-500 italic">
									{loading ? "Loading..." : "No invoices found"}
								</td>
							</tr>
						)}
						{paginatedInvoices.map((inv, idx) => (
							<React.Fragment key={inv.Id || idx}>
								<tr className="hover:bg-gray-300 even:bg-gray-200">
									<td className="px-3 py-2">{idx + 1}</td>
            <td className="px-3 py-2 text-center">{inv.customerName}</td>
            <td className="px-3 py-2 text-center">{inv.itemName}</td>
            <td className="px-3 py-2 text-center">{inv.status ?? "-"}</td>
            <td className="px-3 py-2 text-center">{inv.id ?? "-"}</td>
            <td className="px-3 py-2 text-center">{inv.warehouse}</td>
            <td className="px-3 py-2 text-center">{inv.region ?? "-"}</td>
									        <td className="px-3 py-2 text-right">
                                              {Number(inv.total || 0) === 0 ? (
                                                <span className="bg-yellow-600 text-white p-1 rounded">Add items</span>
                                              ) : (
                                                `${Number(inv.total).toLocaleString()} AED`
                                              )}
                                            </td>

									            <td className="px-3 py-2 text-center">
                                                  {Number(inv.total || 0) === 0 ? (
                                                    <span className="bg-yellow-600 text-white p-1 rounded">Click PDF</span>
                                                  ) : (
                                                    `${(inv.total * 0.05).toLocaleString()} AED`
                                                  )}
                                                </td>
									<td className="px-3 py-2 text-right">
										
										{new Date(inv.createdAt).toLocaleDateString("en-GB", {
											day: "numeric",
											month: "long",
											year: "numeric"
										})}
										
									</td>
									<td className="px-3 py-2 text-center">
										<button
											className="px-2 py-1 mb-1 text-sm text-white bg-yellow-600 rounded-md hover:bg-yellow-700 cursor-pointer mr-1"
											onClick={() => setShowPDFSection((prev) => (prev === idx ? null : idx))}
										>
											PDF
										</button>
										<button
											className="px-2 py-1 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 cursor-pointer"
											onClick={() => handleDelete(inv.id)}
										>
											Delete
										</button>
									</td>
								</tr>

								{showPDFSection === idx && (
                            <Suspense fallback={null}>
									<InvoicePDFForm
										invoice={inv}
										onClose={() => setShowPDFSection(null)}

											onInvUpdate={(updatedTotal) => {
    											setInvoices(prev =>
    												prev.map(item =>
    													item.id === inv.id ? { ...item, total: updatedTotal } : item
    												)
    											);
    										}}
									/>
									</Suspense>
								)}
							</React.Fragment>
						))}
					</tbody>
				</table>
			</div>

			{/* Pagination */}
			{totalPages > 1 && (
				<div className="flex items-center justify-center gap-2 mt-5">
					<button
						className="px-3 py-1 text-sm rounded-md border disabled:opacity-50 cursor-pointer"
						onClick={() => setPage((p) => Math.max(p - 1, 1))}
						disabled={page === 1}
					>
						Prev
					</button>
					<span className="text-sm text-gray-700">
						Page {page} of {totalPages}
					</span>
					<button
						className="px-3 py-1 text-sm rounded-md border disabled:opacity-50 cursor-pointer"
						onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
						disabled={page === totalPages}
					>
						Next
					</button>
				</div>
			)}

			{/* Modal */}
			{showModal && (
	<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
		<div className="bg-white w-full max-w-md rounded-md shadow-lg p-6 relative">
			<h2 className="text-lg font-semibold mb-4">Add New Invoice</h2>
			<div className="flex flex-col gap-3">
				{Object.keys(newInvoice).map((field) => {
					if (["createdAt", "total"].includes(field)) return null;

					// Custom select for "status"
					if (field === "status") {
						return (
							<select
								key={field}
								className={`px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200 ${
									errors.status ? "border-red-500" : "border-gray-300"
								}`}
								value={newInvoice.status}
								onChange={(e) => setNewInvoice({ ...newInvoice, status: e.target.value })}
							>
								<option value="">Select Payment Term</option>
								<option value="30 Days PDC">30 Days PDC</option>
								<option value="60 Days PDC">60 Days PDC</option>
								<option value="120 Days PDC">120 Days PDC</option>
								<option value="Cash on Delivery">Cash on Delivery</option>
								<option value="Bank Transfer">Bank Transfer</option>
							</select>
						);
					}

					// Default text inputs
					return (
						<input
							key={field}
							type="text"
							placeholder={fieldLabels[field]}
							className={`px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200 ${
								errors[field] ? "border-red-500" : "border-gray-300"
							}`}
							value={newInvoice[field]}
							onChange={(e) => setNewInvoice({ ...newInvoice, [field]: e.target.value })}
						/>
					);
				})}
			</div>

			<div className="flex justify-end gap-2 mt-4">
				<button
					className="px-4 py-2 rounded-md border hover:bg-gray-100 cursor-pointer"
					onClick={() => {
						setShowModal(false);
						setErrors({});
					}}
				>
					Cancel
				</button>
				<button
					className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
					onClick={() => handleAdd(newInvoice)}
				>
					Add
				</button>
			</div>

			<button
				className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 cursor-pointer"
				onClick={() => {
					setShowModal(false);
					setErrors({});
				}}
			>
				✕
			</button>
		</div>
	</div>
)}


			{/* Staging Table */}
			<StagingTable
				ref={stagingRef}
				visible={showStaging}
				setVisible={setShowStaging}
				apiProp={`${apiBaseUrl}/api/invoices/batch`}
				entryDict={Object.fromEntries(
					Object.keys(newInvoice)
					.filter(key => key !== "createdAt" && key !== "total")
					.map((key) => {
						let type = "input";
						let options;
						const label = fieldLabels[key];
						
						if (key === "status") {
								type = "select";
								options = ["30 Days PDC", "60 Days PDC", "120 Days PDC", "Cash on Delivery", "Bank Transfer"];
							}
						
						return [key, { type, options, label }];
					})
				)}
			/>
		</div>
	);
};

export default InvoicesTable;
